import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnInit,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { of, Subscription, timer } from 'rxjs';
import { catchError, switchMap, takeWhile } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../auth/auth.service';
import {
  IQuerySubmission,
  isDisplayableResultStatus,
  isFailedQueryStatus,
  isPartialQueryStatus,
  isTerminalQueryStatus,
} from '../../models/IQuerySubmission';
import { QueryPlanData, QueryPlanNode, QueryPlanWarning } from '../../models/IQueryPlan';
import { SqlQueryService } from '../../services/sql-query.service';
import { QueryLimits, QueueStatsService } from '../../services/queue-stats.service';

@Component({
  selector: 'app-query-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    MatTooltipModule,
    CommonModule,
  ],
  templateUrl: './query-form.component.html',
  styleUrl: './query-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QueryFormComponent implements OnInit, OnDestroy {
  @ViewChild('queryEditor') private queryEditor?: ElementRef<HTMLTextAreaElement>;

  private fb = inject(FormBuilder);
  private queryService = inject(SqlQueryService);
  private snackBar = inject(MatSnackBar);
  private cdr = inject(ChangeDetectorRef);
  private auth = inject(AuthService);
  private router = inject(Router);
  private queueStatsService = inject(QueueStatsService);

  private readonly lastSubmittedQueryStorageKey = 'maqi:lastSubmittedQuery';
  readonly pollingIntervalMs = 3000;
  readonly maxPollingMs = 30000 * 1000000;
  readonly demoButtonFlashMs = 220;
  readonly queryFieldPulseMs = 650;
  readonly queryFieldScrollMs = 260;
  readonly planMetricHelp = {
    totalCost:
      "PostgreSQL's estimated work to return all rows, in abstract planner units—not milliseconds or dollars. Use it to compare alternative plans under the same database settings; lower is generally cheaper.",
    estimatedRows:
      'Estimated rows returned by the whole plan, based on database statistics. This is not an actual row count and can be inaccurate.',
    nodeCount:
      'Number of operations in the plan tree, such as scans, joins, sorts, and aggregates. More nodes means a more complex plan, not necessarily a slower one.',
    maxDepth:
      'Deepest number of nested plan operations. Greater depth indicates more nesting, but is not inherently good or bad.',
  } as const;
  readonly planNodeCostHelp =
    'Cost is shown as startup..total: estimated planner work before the first row, then for all rows. These are abstract units, not elapsed time.';
  readonly exampleQueries: { sql: string; buttonLabel: string }[] = [
    {
      sql: 'SELECT * FROM mpc_orbits LIMIT 10;',
      buttonLabel: 'Recent MPC orbits',
    },
    {
      sql: 'SELECT unpacked_primary_provisional_designation, epoch_mjd, q FROM mpc_orbits ORDER BY epoch_mjd DESC LIMIT 25;',
      buttonLabel: 'Latest perihelion epochs',
    },
    {
      sql: 'SELECT * FROM obs_sbn WHERE mag < 20 LIMIT 50;',
      buttonLabel: 'Bright observations',
    },
    {
      sql: `SELECT obstime, permid, provid, band, photap, mag, rmsmag
FROM obs_sbn
WHERE obstime >= TIMESTAMP '2026-06-01 00:00:00'
  AND obstime < TIMESTAMP '2026-07-01 00:00:00'
ORDER BY obstime
LIMIT 50;`,
      buttonLabel: 'Observations in a date range',
    },
    //
    {
      sql: 'SELECT * FROM neocp_els where digest2 > 99;',
      buttonLabel: 'Find the highest digest2 scoring NEOCP entries',
    },
    {
      sql: "SELECT * FROM neocp_prev_des where iau_desig like '%2026 A%'",
      buttonLabel:
        'Find objects that were on the neocp previously, with a designation in a particular half-month',
    },
  ];

  queryForm = this.fb.nonNullable.group({
    query: [environment.DEFAULT_QUERY, [Validators.required, Validators.minLength(10)]],
  });

  isSubmitting = false;
  errorMessage: string | null = null;
  querySubmission: IQuerySubmission | null = null;
  hasSubmitted = false;
  isPlanning = false;
  planErrorMessage: string | null = null;
  queryPlan: QueryPlanData | null = null;
  activeExampleQuery: string | null = null;
  isQueryFieldPulsing = false;
  queryLimits: QueryLimits | null = null;

  private pollSub: Subscription | null = null;
  private planSub: Subscription | null = null;
  private queryValueSub: Subscription | null = null;
  private queryLimitsSub: Subscription | null = null;
  private queryLimitsRefreshSub: Subscription | null = null;
  private demoButtonFlashTimeout: ReturnType<typeof setTimeout> | null = null;
  private queryFieldPulseTimeout: ReturnType<typeof setTimeout> | null = null;
  private queryFocusTimeout: ReturnType<typeof setTimeout> | null = null;
  private lastPlannedQuery: string | null = null;
  private isDestroyed = false;
  private renderQueued = false;

  ngOnInit(): void {
    this.restoreLastSubmittedQuery();
    this.queryLimits = this.queueStatsService.currentQueryLimits;
    this.queryLimitsSub = this.queueStatsService.queryLimits$.subscribe((limits) => {
      this.queryLimits = limits;
      this.mark();
    });
    this.queryValueSub = this.queryForm.controls.query.valueChanges.subscribe(() => {
      if (this.planErrorMessage) this.planErrorMessage = null;
      this.mark();
    });
    this.queryLimitsRefreshSub = this.queueStatsService.refreshQueryLimits().subscribe({
      error: () => {
        // Queue stats are informational here; query submission should not depend on them.
      },
    });
  }

  submitQuery(): void {
    if (this.queryForm.invalid) return;
    this.resetState();
    this.isSubmitting = true;
    this.hasSubmitted = true;
    const sql = this.queryForm.controls.query.value.trim();
    this.querySubmission = this.buildPendingSubmission(sql);

    this.queryService.submitQuery(sql).subscribe({
      next: (submission) => {
        this.saveLastSubmittedQuery(sql);
        this.querySubmission = submission;
        this.isSubmitting = false;
        this.startPolling(submission);
        this.mark();
      },
      error: (err) => {
        const message = this.describeError(err);
        this.isSubmitting = false;
        this.errorMessage = message;
        this.openSnackBar(message, true);
        // If token expired/unauthorized, force re-login UX
        const code = err?.status;
        const errMsg = err?.error?.error || err?.error?.message || '';
        if (code === 401 || code === 403 || /token has expired/i.test(errMsg)) {
          this.querySubmission = null;
          this.hasSubmitted = false;
          this.openSnackBar('Session expired. Please sign in again.', true);
        } else {
          this.hasSubmitted = false;
          this.querySubmission = null;
        }
        this.mark();
      },
    });
  }

  planQuery(): void {
    if (this.queryForm.invalid) {
      this.queryForm.markAllAsTouched();
      this.mark();
      return;
    }

    const sql = this.queryForm.controls.query.value.trim();
    if (!sql || this.isPlanning) return;

    this.planSub?.unsubscribe();
    this.isPlanning = true;
    this.planErrorMessage = null;
    this.mark();

    this.planSub = this.queryService.planQuery(sql).subscribe({
      next: (response) => {
        this.queryPlan = response.data;
        this.lastPlannedQuery = response.data.query || sql;
        this.planErrorMessage = null;
        this.isPlanning = false;
        this.mark();
      },
      error: (err) => {
        this.planErrorMessage = this.describeError(err);
        this.isPlanning = false;
        this.mark();
      },
    });
  }

  private startPolling(submission: IQuerySubmission) {
    this.pollSub?.unsubscribe();
    const start = Date.now();

    this.pollSub = timer(1000, this.pollingIntervalMs)
      .pipe(
        switchMap(() =>
          this.queryService.fetchSubmissionMeta(this.querySubmission ?? submission).pipe(
            catchError((err) => {
              const code = err?.status || err?.name;
              const withinWindow = Date.now() - start <= this.maxPollingMs;
              if (withinWindow && (code === 404 || code === 'NoSuchKey' || code === 400)) {
                const pendingSnapshot = this.querySubmission ?? submission;
                return of({ ...pendingSnapshot });
              }
              throw err;
            }),
          ),
        ),
        takeWhile(() => Date.now() - start <= this.maxPollingMs, true),
      )
      .subscribe({
        next: (meta) => {
          const merged: IQuerySubmission = {
            ...(this.querySubmission ?? submission),
            ...meta,
            status: (meta.status || this.querySubmission?.status || '').trim().toLowerCase() as any,
          };
          this.querySubmission = merged;
          const status = merged.status;
          if (isDisplayableResultStatus(status)) {
            this.stopPolling();
            void this.navigateToResults(merged);
          } else if (isFailedQueryStatus(status)) {
            this.handleErrorState(merged, merged.error_message || 'Query failed.');
          }
          this.mark();
        },
        error: (err) => {
          this.errorMessage = 'Failed to poll query status.';
          this.isSubmitting = false;
          this.stopPolling();
          this.handleErrorState(
            {
              ...(this.querySubmission ?? submission),
              status: 'error',
            } as IQuerySubmission,
            this.describeError(err),
          );
          this.mark();
        },
        complete: () => {
          const status = (this.querySubmission?.status || '').trim().toLowerCase();
          if (!isTerminalQueryStatus(status) && !this.errorMessage) {
            this.handleErrorState(
              {
                ...(this.querySubmission ?? submission),
                status: 'error',
              } as IQuerySubmission,
              'Query status did not update in time. Please retry.',
            );
            this.mark();
          }
        },
      });
  }

  private stopPolling() {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
  }

  clearResults() {
    this.querySubmission = null;
    this.errorMessage = null;
    this.hasSubmitted = false;
    this.stopPolling();
    this.mark();
  }

  isPendingStatus(status: string | null | undefined): boolean {
    const normalized = (status || '').toLowerCase();
    return (
      normalized === 'pending' ||
      normalized === 'queued' ||
      normalized === 'running' ||
      normalized === 'processing'
    );
  }

  isProcessingStatus(status: string | null | undefined): boolean {
    const normalized = (status || '').toLowerCase();
    return normalized === 'running' || normalized === 'processing';
  }

  shouldShowStreamedRows(submission: IQuerySubmission | null): boolean {
    return (
      this.isProcessingStatus(submission?.status) &&
      typeof submission?.result_row_count === 'number'
    );
  }

  streamedRowsLabel(submission: IQuerySubmission | null): string {
    const rowCount = submission?.result_row_count;
    if (typeof rowCount !== 'number') return '';
    const formattedRows = this.formatNumber(rowCount);
    const maxRows = this.queryLimits?.max_result_rows;
    const rowLabel = `${formattedRows} row${rowCount === 1 ? '' : 's'}`;
    return typeof maxRows === 'number'
      ? `${rowLabel} / ${this.formatNumber(maxRows)} max`
      : rowLabel;
  }

  shouldShowResultMessage(submission: IQuerySubmission | null): boolean {
    return this.isProcessingStatus(submission?.status) && !!submission?.result_message;
  }

  queryLimitsLabel(): string {
    if (!this.queryLimits) return 'Limits loading...';
    return `Limits: ${this.formatDuration(this.queryLimits.max_time_seconds)} max runtime · ${this.formatNumber(this.queryLimits.max_result_rows)} max rows · batches up to ${this.formatNumber(this.queryLimits.batch_size)}`;
  }

  processingStartedLabel(submission: IQuerySubmission | null): string | null {
    const started = this.parseCreationTime(submission?.processing_start_time || '');
    return started ? started.toLocaleString() : null;
  }

  queueTimeLabel(submission: IQuerySubmission | null): string | null {
    const queuedSeconds = this.finiteSeconds(submission?.queued_duration_seconds);
    if (queuedSeconds !== null) return this.formatDuration(queuedSeconds);

    const fallbackSeconds = this.computedQueueSeconds(submission);
    return fallbackSeconds === null ? null : this.formatDuration(fallbackSeconds);
  }

  processingTimeLabel(submission: IQuerySubmission | null): string | null {
    const processingSeconds = this.finiteSeconds(submission?.processing_duration_seconds);
    if (processingSeconds !== null) return this.withRuntimeLimit(processingSeconds);

    const fallbackSeconds = this.computedProcessingSeconds(submission);
    return fallbackSeconds === null ? null : this.withRuntimeLimit(fallbackSeconds);
  }

  streamingProgressDetail(submission: IQuerySubmission | null): string | null {
    if (!this.isProcessingStatus(submission?.status)) return null;
    const rowCount = submission?.result_row_count;
    const batchSize = this.queryLimits?.batch_size;
    if (rowCount === 0 && typeof batchSize === 'number') {
      return `Streaming rows... waiting for first batch of up to ${this.formatNumber(batchSize)} rows`;
    }
    if (rowCount === null || rowCount === undefined) {
      return 'Query is running; row-count progress is not available yet.';
    }
    if (typeof batchSize === 'number') {
      return `Progress updates in batches of up to ${this.formatNumber(batchSize)} rows.`;
    }
    return null;
  }

  isPartialStatus(status: string | null | undefined): boolean {
    return isPartialQueryStatus(status);
  }

  isPlanStale(): boolean {
    if (!this.queryPlan || !this.lastPlannedQuery) return false;
    return this.queryForm.controls.query.value.trim() !== this.lastPlannedQuery.trim();
  }

  planMetricLabel(value: number | null | undefined, digits = 0): string {
    if (typeof value !== 'number' || !Number.isFinite(value)) return 'n/a';
    return value.toLocaleString(undefined, {
      maximumFractionDigits: digits,
      minimumFractionDigits: value % 1 === 0 ? 0 : Math.min(digits, 2),
    });
  }

  planCostRange(node: QueryPlanNode): string {
    const startup = this.planMetricLabel(node.startup_cost, 2);
    const total = this.planMetricLabel(node.total_cost, 2);
    if (startup === 'n/a') return total;
    if (total === 'n/a') return startup;
    return `${startup}..${total}`;
  }

  planNodeTitle(node: QueryPlanNode): string {
    const relation = node.relation_name ? ` on ${node.relation_name}` : '';
    const alias = node.alias && node.alias !== node.relation_name ? ` as ${node.alias}` : '';
    return `${node.node_type || 'Plan node'}${relation}${alias}`;
  }

  hasNodeDetails(node: QueryPlanNode): boolean {
    return !!(
      node.join_type ||
      node.index_name ||
      node.hash_condition ||
      node.merge_condition ||
      node.index_condition ||
      node.filter
    );
  }

  rawPlanJson(): string {
    try {
      return JSON.stringify(this.queryPlan?.raw_plan ?? null, null, 2);
    } catch {
      return String(this.queryPlan?.raw_plan ?? '');
    }
  }

  trackChip(_: number, value: string): string {
    return value;
  }

  trackWarning(index: number, warning: QueryPlanWarning): string {
    return `${warning.code}:${warning.node_type}:${warning.relation_name || ''}:${index}`;
  }

  private handleErrorState(submission: IQuerySubmission, message: string) {
    this.querySubmission = { ...submission, error_message: message };
    this.openSnackBar(message, true);
    this.isSubmitting = false;
    this.stopPolling();
  }

  formatCreationTime(creationTime: string | undefined): string {
    if (!creationTime) return '';
    const parsed = this.parseCreationTime(creationTime);
    if (!parsed) return creationTime;
    return parsed.toLocaleString();
  }

  applyExample(query: string): void {
    this.queryForm.patchValue({ query });
    this.flashExampleQuery(query);
    this.pulseQueryField();
    this.focusQueryEditor(query.length);
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.stopPolling();
    this.planSub?.unsubscribe();
    this.queryValueSub?.unsubscribe();
    this.queryLimitsSub?.unsubscribe();
    this.queryLimitsRefreshSub?.unsubscribe();
    this.clearDemoFeedbackTimers();
  }

  private buildPendingSubmission(query: string): IQuerySubmission {
    const userEmail = this.auth.user()?.email || 'user';
    return {
      job_id: '',
      query,
      status: 'pending',
      username: userEmail,
      creation_time: new Date().toISOString(),
    };
  }

  private describeError(err: any): string {
    const message =
      err?.error?.message ||
      err?.error?.error ||
      err?.message ||
      'Something went wrong. Please try again.';
    return message;
  }

  private resetState() {
    this.errorMessage = null;
    this.querySubmission = null;
    this.stopPolling();
  }

  private flashExampleQuery(query: string): void {
    if (this.demoButtonFlashTimeout) {
      clearTimeout(this.demoButtonFlashTimeout);
      this.demoButtonFlashTimeout = null;
    }

    this.activeExampleQuery = null;
    this.mark();

    queueMicrotask(() => {
      this.activeExampleQuery = query;
      this.mark();
      this.demoButtonFlashTimeout = setTimeout(() => {
        this.activeExampleQuery = null;
        this.demoButtonFlashTimeout = null;
        this.mark();
      }, this.demoButtonFlashMs);
    });
  }

  private pulseQueryField(): void {
    if (this.queryFieldPulseTimeout) {
      clearTimeout(this.queryFieldPulseTimeout);
      this.queryFieldPulseTimeout = null;
    }

    this.isQueryFieldPulsing = false;
    this.mark();

    queueMicrotask(() => {
      this.isQueryFieldPulsing = true;
      this.mark();
      this.queryFieldPulseTimeout = setTimeout(() => {
        this.isQueryFieldPulsing = false;
        this.queryFieldPulseTimeout = null;
        this.mark();
      }, this.queryFieldPulseMs);
    });
  }

  private focusQueryEditor(selectionStart: number): void {
    if (this.queryFocusTimeout) {
      clearTimeout(this.queryFocusTimeout);
      this.queryFocusTimeout = null;
    }

    this.queryFocusTimeout = setTimeout(() => {
      const editor = this.queryEditor?.nativeElement;
      if (!editor) return;
      const prefersReducedMotion =
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      editor.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'center',
        inline: 'nearest',
      });

      this.queryFocusTimeout = setTimeout(
        () => {
          try {
            editor.focus({ preventScroll: true });
          } catch {
            editor.focus();
          }
          editor.setSelectionRange(selectionStart, selectionStart);
          this.queryFocusTimeout = null;
        },
        prefersReducedMotion ? 0 : this.queryFieldScrollMs,
      );
    }, 0);
  }

  private clearDemoFeedbackTimers(): void {
    if (this.demoButtonFlashTimeout) clearTimeout(this.demoButtonFlashTimeout);
    if (this.queryFieldPulseTimeout) clearTimeout(this.queryFieldPulseTimeout);
    if (this.queryFocusTimeout) clearTimeout(this.queryFocusTimeout);
    this.demoButtonFlashTimeout = null;
    this.queryFieldPulseTimeout = null;
    this.queryFocusTimeout = null;
  }

  private openSnackBar(message: string | null, isError = false) {
    if (!message) return;
    this.snackBar.open(message, 'Dismiss', {
      duration: 6000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: isError ? ['query-snackbar', 'query-snackbar-error'] : ['query-snackbar'],
    });
  }

  private mark() {
    this.cdr.markForCheck();
    this.renderNow();
  }

  private renderNow(): void {
    if (this.isDestroyed || this.renderQueued) return;
    this.renderQueued = true;
    queueMicrotask(() => {
      this.renderQueued = false;
      if (this.isDestroyed) return;
      this.cdr.detectChanges();
    });
  }

  private restoreLastSubmittedQuery(): void {
    const savedQuery = this.readLastSubmittedQuery();
    if (!savedQuery) return;
    this.queryForm.patchValue({ query: savedQuery }, { emitEvent: false });
  }

  private readLastSubmittedQuery(): string | null {
    try {
      const value = globalThis.sessionStorage?.getItem(this.lastSubmittedQueryStorageKey)?.trim();
      return value || null;
    } catch {
      return null;
    }
  }

  private saveLastSubmittedQuery(query: string): void {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;
    try {
      globalThis.sessionStorage?.setItem(this.lastSubmittedQueryStorageKey, trimmedQuery);
    } catch {
      // Restoring the editor is a convenience only; query submission should never depend on storage.
    }
  }

  private async navigateToResults(submission: IQuerySubmission): Promise<void> {
    const jobId = (submission.job_id || '').trim();
    if (!jobId) {
      this.handleErrorState(
        submission,
        'Query completed but no job ID was returned. Please retry.',
      );
      this.mark();
      return;
    }

    try {
      await this.router.navigate(['/results', jobId]);
    } catch {
      this.handleErrorState(
        submission,
        'Query completed, but opening the results page failed. Please retry.',
      );
      this.mark();
    }
  }

  private parseCreationTime(input: string): Date | null {
    const trimmed = (input || '').trim();
    if (!trimmed) return null;

    const numericValue = Number(trimmed);
    if (Number.isFinite(numericValue)) {
      const milliseconds = numericValue < 1e11 ? numericValue * 1000 : numericValue;
      const dateFromNumeric = new Date(milliseconds);
      if (!Number.isNaN(dateFromNumeric.getTime())) return dateFromNumeric;
    }

    const dateFromString = new Date(trimmed);
    if (!Number.isNaN(dateFromString.getTime())) return dateFromString;
    return null;
  }

  private computedQueueSeconds(submission: IQuerySubmission | null): number | null {
    const created = this.parseCreationTime(submission?.creation_time || '');
    const started = this.parseCreationTime(submission?.processing_start_time || '');
    if (!created || !started) return null;
    return Math.max(0, (started.getTime() - created.getTime()) / 1000);
  }

  private computedProcessingSeconds(submission: IQuerySubmission | null): number | null {
    const started = this.parseCreationTime(submission?.processing_start_time || '');
    if (!started || !this.isProcessingStatus(submission?.status)) return null;
    return Math.max(0, (Date.now() - started.getTime()) / 1000);
  }

  private finiteSeconds(value: number | null | undefined): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }

  private withRuntimeLimit(elapsedSeconds: number): string {
    const runtime = this.formatDuration(elapsedSeconds);
    const maxTime = this.queryLimits?.max_time_seconds;
    return typeof maxTime === 'number' ? `${runtime} / ${this.formatDuration(maxTime)} max` : runtime;
  }

  private formatDuration(valueSeconds: number): string {
    if (!Number.isFinite(valueSeconds)) return 'n/a';
    const totalSeconds = Math.max(0, Math.round(valueSeconds));
    if (totalSeconds < 60) return `${totalSeconds}s`;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes < 60) return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  }

  private formatNumber(value: number): string {
    return Math.round(value).toLocaleString();
  }
}
